import { useState, type CSSProperties } from "react";
import { PageHeader, Avatar, Badge, Icon, ghostBtn, primaryBtn, pgKickerStat } from "@/components/admin-ui";
import { AuthorDrawer } from "@/components/AuthorDrawer";
import { useAuthors, type EditorialRole } from "@/lib/authors-hooks";

// Authors page — every pro user is a contributor by default. Click a
// card to open the drawer for stats, recent articles, role toggle,
// and suspend.

export function AuthorsPage() {
  const { authors } = useAuthors();
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        kicker="Editorial"
        title="Authors & desks"
        sub="Every pro user with publishing rights. Click a card to open stats, recent articles, and manage role / suspend."
        actions={
          <>
            <button style={ghostBtn} type="button">Desks &amp; permissions</button>
            <button style={primaryBtn} type="button">{Icon.plus} Invite contributor…</button>
          </>
        }
      />

      <div style={{ padding: "0 26px 26px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {authors.map((entry) => {
            const a = entry.display;
            const s = entry.stats;
            const role: EditorialRole = entry.wire.editorial_role;
            const suspended = entry.wire.status === "suspended";
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => setOpenId(entry.id)}
                style={card}
                className="ops-row"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar initials={a.initials} tint={a.tint} size={44} title={a.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 500,
                        color: "var(--ink)", letterSpacing: "-0.015em",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {a.name}
                    </div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>
                      {a.team} · last active {s.last}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 14, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8,
                    paddingTop: 14, borderTop: "1px solid var(--rule)",
                  }}
                >
                  <Stat label="Drafts"    value={s.drafts} />
                  <Stat label="In review" value={s.awaiting} hot={s.awaiting > 0} />
                  <Stat label="Published" value={s.published} />
                  <Stat label="Leads 30d" value={s.leads} accent />
                </div>

                <div
                  style={{
                    marginTop: 12, display: "flex", justifyContent: "space-between",
                    alignItems: "center", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)",
                  }}
                >
                  <span>{s.views.toLocaleString()} views · 30d</span>
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    {suspended && <Badge tone="neg">Suspended</Badge>}
                    {role === "editor"
                      ? <Badge tone="info">Editor</Badge>
                      : <Badge tone="ai">Contributor</Badge>}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {authors.length === 0 && (
          <div
            style={{
              padding: "48px 0", textAlign: "center",
              fontFamily: "var(--font-serif)", fontStyle: "italic",
              color: "var(--ink-3)", fontSize: 14,
            }}
          >
            No pro users yet. Authors appear here as soon as someone signs up with the agent role.
          </div>
        )}
      </div>

      {openId && <AuthorDrawer authorId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function Stat({ label, value, hot, accent }: { label: string; value: number; hot?: boolean; accent?: boolean }) {
  const fg = hot ? "#8a5a00" : accent ? "var(--accent)" : "var(--ink)";
  return (
    <div>
      <div style={{ ...pgKickerStat, fontSize: 9 }}>{label}</div>
      <div
        style={{
          marginTop: 4, fontFamily: "var(--font-serif)", fontSize: 19, fontWeight: 500,
          letterSpacing: "-0.018em", color: fg, lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

const card: CSSProperties = {
  padding: "16px 18px", borderRadius: 12,
  border: "1px solid var(--rule)", background: "var(--surface)",
  cursor: "pointer", textAlign: "left",
  fontFamily: "inherit", color: "inherit", width: "100%",
  display: "block",
};
